import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import Box from '@/modules/components/Box'
import Text from '@/modules/components/Typography'

import { emailAxios } from '../utils/axios'

declare global {
  interface Window {
    initGeetest: InitGeetest
  }
}

// See https://docs.geetest.com/captcha/apirefer/api/web/ for config descriptions
type GeetestConfig = {
  gt: string
  challenge: string
  offline: boolean
  new_captcha: boolean

  // default to popup
  product?: 'popup' | 'float' | 'custom' | 'bind'
  lang?: string
  width?: string
  timeout?: number
  https?: boolean
  hideSuccess?: boolean
  hideClose?: boolean
  hideRefresh?: boolean
}

export type GeetestVerificationResult = {
  geetest_challenge: string
  geetest_validate: string
  geetest_seccode: string
}

export type GeetestInstance = {
  appendTo: (id: string) => void
  bindForm: (id: string) => void
  onReady: (callback: () => void) => void
  onSuccess: (callback: () => void) => void
  getValidate: () => GeetestVerificationResult
  reset: () => void
  verify: () => void
}

type InitGeetest = (
  config: GeetestConfig,
  onSuccess: (captchaInstance: GeetestInstance) => void
) => void

type GeetestRegisterResponse = {
  challenge: string
  live: boolean
}

declare const initGeetest: InitGeetest

const useGeetest = (
  onVerifySuccess: GeetestComponentProps['onVerifySuccess'],
  geetestConfig?: Partial<GeetestConfig>
) => {
  const onVerifySuccessRef =
    useRef<GeetestComponentProps['onVerifySuccess']>(onVerifySuccess)
  onVerifySuccessRef.current = onVerifySuccess
  const configRef = useRef<Partial<GeetestConfig> | undefined>(geetestConfig)
  configRef.current = geetestConfig

  const [geetestInstance, setGeetestInstance] = useState<
    GeetestInstance | undefined
  >(undefined)

  const initialize = useCallback(async () => {
    const url = `/captcha/register`

    await emailAxios.get<GeetestRegisterResponse>(url).then((response) => {
      // Ensure data.gt, data.challenge and data.success are not null
      const { data } = response
      initGeetest(
        {
          // required config
          gt: 'cb4268329fff49fcd61a333ce0f23352',
          challenge: data.challenge,
          offline: !data.live,
          new_captcha: true,

          // optional config
          https: true,
          product: 'float',
          width: '100%',
          lang: 'en',
          ...(configRef.current || {}),
        },
        (instance) => {
          // Call instance method captchaObj
          setGeetestInstance(instance)
          instance.appendTo('#captcha')
          instance.onReady(() => {
            document.querySelector('#geetest_loading')?.remove()

            instance.onSuccess(() => {
              const result = instance.getValidate()
              if (!result || !onVerifySuccessRef.current) return

              onVerifySuccessRef.current(result)
            })
          })
        }
      )
    })
  }, [])

  useEffect(() => {
    initialize()
  }, [initialize])

  return geetestInstance
}

type GeetestComponentProps = {
  geetestConfig?: Partial<GeetestConfig>
  onVerifySuccess?: (val: GeetestVerificationResult) => void
}

export const GeetestComponent = forwardRef(
  (
    { onVerifySuccess, geetestConfig, ...restProps }: GeetestComponentProps,
    ref
  ) => {
    const geetestInstance = useGeetest(onVerifySuccess, geetestConfig)
    useImperativeHandle(ref, () => geetestInstance)

    return (
      <Box id="captcha" {...restProps}>
        <Box className="geetest_form">
          <input type="hidden" name="geetest_challenge" />
          <input type="hidden" name="geetest_validate" />
          <input type="hidden" name="geetest_seccode" />
        </Box>
        {geetestConfig?.product !== 'bind' && (
          <Text
            height="42px"
            textAlign="center"
            color="#626973"
            id="geetest_loading"
          >
            Loading Captcha......
          </Text>
        )}
      </Box>
    )
  }
)

GeetestComponent.displayName = 'GeetestComponent'
